import { useState, useEffect, useRef } from "react";
import FLOAT_MENU from "./FLOAT_MENU";

// Updated to match the CSS max-height
const MAX_HEIGHT = "384px";

const Icon = () => {
  return (
    <svg
      style={{
        width: "16px",
        height: "16px",
        marginLeft: "8px",
        flexShrink: 0,
      }}
      aria-hidden="true"
      fill="currentColor"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"></path>
    </svg>
  );
};

const Dropdown = ({
  value: propValue,
  children,
  is_multi,
  onChange,
  className = "",
  filter_on,
  placeholder,
  is_enable_empty,
  height,
  array_value,
  option_style,
  ...props
}) => {
  const defaultPlaceholder = placeholder || "Select";

  const inputRef = useRef(null);
  const searchRef = useRef(null);

  // eslint-disable-next-line no-unused-vars
  const [filter_text, setFilterText] = useState("");
  const [filtered_children, setFilteredChildren] = useState([]);
  const [is_active, setIsActive] = useState(false);
  const [value, setValue] = useState("");
  const [anchor, setAnchor] = useState(null);

  const convertChildren = (unknown_children) => {
    if (
      typeof unknown_children === "object" &&
      !Array.isArray(unknown_children) &&
      unknown_children !== null
    ) {
      return [unknown_children];
    } else {
      return unknown_children;
    }
  };

  useEffect(() => {
    let selected = [];
    if (is_multi && Array.isArray(propValue)) {
      selected = propValue;
    } else if (!is_multi && typeof propValue !== "object") {
      selected = propValue || "";
    }

    setValue(selected);
    setFilteredChildren(convertChildren(children));
    setFilterText("");
    setIsActive(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (is_active && searchRef?.current) {
      searchRef?.current?.focus();
      setFilterText("");
    }
  }, [is_active]);

  useEffect(() => {
    if (Array.isArray(propValue) && Array.isArray(value) && is_multi) {
      if (propValue?.length !== value?.length) {
        setValue(propValue);
      }
    } else if (propValue !== value) {
      setValue(propValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propValue, is_multi]);

  const floatMenuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (
        (inputRef.current && inputRef.current.contains(e.target)) ||
        (floatMenuRef.current && floatMenuRef.current.contains(e.target))
      ) {
        return;
      }
      setIsActive(false);
    };

    window.addEventListener("click", handler);
    return () => {
      window.removeEventListener("click", handler);
    };
  }, []);

  const filterOnChange = (e) => {
    const keywords = e?.target?.value?.toLowerCase();
    const regex_string = `${keywords
      ?.split(" ")
      ?.map((word) => `(?=.*?${word})`)
      ?.join("")}.*`;
    const regex_pattern = new RegExp(regex_string);
    if (keywords) {
      const filtered = convertChildren(children).filter((item) => {
        const childContent = item?.props?.children;
        const textContent =
          typeof childContent === "string"
            ? childContent
            : typeof childContent === "number"
              ? String(childContent)
              : childContent?.props?.children || "";
        return regex_pattern.test(String(textContent).toLowerCase());
      });
      setFilteredChildren(filtered);
      setFilterText(keywords);
    } else {
      setFilteredChildren(convertChildren(children));
      setFilterText(keywords);
    }
  };

  const toggleDropdown = (event) => {
    setFilteredChildren(convertChildren(children));
    setIsActive(!is_active);
    setAnchor(event?.currentTarget);
  };

  const removeOption = (option) => {
    return value.filter((val) => val !== option);
  };

  const handleClick = (key) => {
    let newValue = "";
    if (is_multi) {
      newValue = key ? [...value, key] : [value];
    } else {
      newValue = key || "";
    }
    setValue(newValue);
    if (onChange) onChange(newValue);

    if (!is_multi) {
      setIsActive(false);
    }
  };

  const onTagRemove = (tagKey) => {
    const newValue = removeOption(tagKey);
    setValue(newValue);
    if (onChange) onChange(newValue);
  };

  const isSelected = (key) => {
    if (is_multi) {
      return Array.isArray(value) && value.includes(key);
    } else {
      return value === key;
    }
  };

  // CHANGED: Base style strictly handles the 2-line clamping.
  // Line-height and max-height strictly enforce the cut-off.
  const baseItemStyle = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    whiteSpace: "normal",
    wordBreak: "break-word",
    textAlign: "left",
    lineHeight: "1.4em", // Explicit line height
    maxHeight: "2.8em", // Exactly 2x line height to prevent 3rd line bleeding
    width: "100%",
  };

  // CHANGED: This wrapper handles the visual appearance (background, padding, etc.)
  const optionWrapperStyle = {
    ...option_style,
    width: "100%",
    padding: "8px",
    fontSize: "13px",
    cursor: "pointer",
    borderRadius: "5px",
    marginBottom: "4px",
    boxSizing: "border-box", // Prevents padding from blowing out the 100% width
  };

  const childrenDropdownStyle = {
    ...baseItemStyle,
    color: "#2475bb",
    backgroundColor: "white",
    fontSize: "12px",
    borderRadius: "3px",
    padding: "4px 8px",
    marginRight: "4px",
  };

  const parentChildrenDropdownStyle = {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
    justifyContent: "flex-start",
    alignItems: "center",
  };

  const options = Array.isArray(filtered_children) && (
    <>
      {filtered_children?.length > 0 &&
        filtered_children?.map((item, idx) => {
          return (
            <div
              key={idx}
              onClick={() => {
                handleClick(item.key);
              }}
              data-mapid={`selectType-${idx}`}
              style={{
                display: "flex",
                justifyContent: "flex-start",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <div
                title={
                  typeof item.props.children === "string"
                    ? item.props.children
                    : ""
                }
                style={{
                  ...optionWrapperStyle,
                  backgroundColor: isSelected(item.key) ? "#e5e7eb" : "#f3f4f6",
                }}
              >
                {/* Wrap the text inside the strict baseItemStyle to enforce clean 2 lines */}
                <div style={baseItemStyle}>{item.props.children}</div>
              </div>
            </div>
          );
        })}
    </>
  );

  const getDisplay = () => {
    if (!value || value?.length === 0) {
      return defaultPlaceholder;
    }
    if (is_multi) {
      if (Array.isArray(value)) {
        return (
          <div style={parentChildrenDropdownStyle}>
            {value?.length >= 0 &&
              value?.map((key, idx) => {
                const label = filtered_children?.find(
                  (child) => child?.key === key,
                )?.props?.children;
                return (
                  <div
                    key={idx}
                    style={childrenDropdownStyle}
                    onClick={(e) => {
                      e.stopPropagation();
                      onTagRemove(key);
                    }}
                  >
                    {label}
                  </div>
                );
              })}
          </div>
        );
      } else {
        return defaultPlaceholder;
      }
    } else {
      if (Array.isArray(filtered_children)) {
        const label = filtered_children?.find((child) => child?.key === value)
          ?.props?.children;
        return label?.props?.children || label;
      }
      return defaultPlaceholder;
    }
  };

  let object_float = {
    maxHeight: MAX_HEIGHT,
    overflowY: "auto",
    overflowX: "hidden", // CHANGED: Added overflowX hidden to fix wide select issue
    paddingRight: "8px",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    width: "100%",
    boxSizing: "border-box",
  };

  const dropdownChildStyle = {
    width: "100%",
    paddingRight: "8px",
    boxSizing: "border-box",
  };

  return (
    <main style={{ display: "flex", justifyContent: "center" }}>
      <section
        {...props}
        className={`noselect background_white outline_grey button_big ${className}`}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: "9px",
          border: "1px solid #d1d5db",
          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
          padding: "10px 16px",
          cursor: "pointer",
          textAlign: "left",
          width: "100%",
          maxWidth: "400px",
          boxSizing: "border-box",
        }}
        onClick={(event) => toggleDropdown(event)}
        ref={inputRef}
      >
        <span style={dropdownChildStyle}>
          {/* Also apply the baseItemStyle to the preview so it limits to 2 lines cleanly */}
          <div style={baseItemStyle}>{getDisplay()}</div>
        </span>
        <span style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          {<Icon />}
        </span>
      </section>

      {is_active && (
        <FLOAT_MENU anchorEl={anchor} open={is_active} onClose={toggleDropdown}>
          <div
            ref={floatMenuRef}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              width: "100%",
              maxWidth: "400px", // CHANGED: Optional, prevents menu from growing past trigger width
              paddingTop: "8px",
              boxSizing: "border-box",
              overflowX: "hidden", // CHANGED: Constrain horizontal growth
            }}
          >
            {filter_on && (
              <input
                className="margin_bottom"
                type="text"
                ref={searchRef}
                onChange={filterOnChange}
                style={{
                  textAlign: "left",
                  maxHeight: "25px",
                  width: "90%",
                  alignSelf: "center",
                  marginBottom: "8px",
                  boxSizing: "border-box",
                }}
              />
            )}
            <div style={object_float}>{options}</div>
          </div>
        </FLOAT_MENU>
      )}
    </main>
  );
};

export default Dropdown;
